import React, { FC } from 'react';

import { WithImpression } from 'src/components/atoms/WithImpression';
import { Row } from 'src/styles/layout';
import { CSSVarTitle, Heading, SubTitle, Title } from 'src/styles/layout.styles';
import {
  trackPartnerClick,
  trackPartnerImpressions,
  trackPartnerProductClick,
} from 'src/tracking/homepageTracking';
import { Image } from 'src/types/Image';
import * as styles from './HomepagePartnerBlock.style';

interface SmallImage {
  image: string;
  link: string;
  productCode: string;
  productId: string;
  title: string;
}

interface LargeImage {
  image: Image;
  link: string;
  title: string;
}

interface Block {
  largeImage: LargeImage;
  link: string;
  smallImages: SmallImage[];
  subtitle: string;
  title: string;
}

export interface HomepagePartners {
  blocks: Block[];
  heading: string;
  subheading: string;
}

export interface PartnerBlockProps extends HomepagePartners {
  sectionId: string;
}

export const PARTNER_SUBTITLE_TESTID = 'homepage_partner_block__sub_title';
export const PARTNER_BLOCK_TESTID = 'homepage_partner_block__partners_block';
export const PARTNER_BLOCK_SMALL_IMAGE_TESTID =
  'homepage_partner_block__partners_block__small_image';

export const HomepagePartnerBlock: FC<PartnerBlockProps> = ({
  sectionId,
  heading,
  subheading,
  blocks,
}: PartnerBlockProps) => {
  return (
    <>
      <h2 css={Heading} data-with-sub-heading="true">
        {heading}
      </h2>
      <p css={styles.PartnerSubTitle} data-testid={PARTNER_SUBTITLE_TESTID}>
        {subheading}
      </p>
      <div css={styles.PartnersRow}>
        <Row>
          {blocks.map((partner, partnerIndex) => {
            return (
              <div
                data-testid={PARTNER_BLOCK_TESTID}
                css={styles.PartnersBlock}
                key={partner.title}
              >
                <div css={styles.PartnersImages}>
                  {/* @ts-ignore*/}
                  <Row.Column size={{ phone: 12, tablet: 8 }}>
                    <a
                      css={styles.PartnersLargeImage}
                      style={
                        {
                          '--bgDesktop': `url(${partner.largeImage.image.desktop})`,
                          '--bgMobile': `url(${partner.largeImage.image.mobile})`,
                        } as styles.CSSVarImage
                      }
                      href={partner.largeImage.link}
                      onMouseDown={() =>
                        trackPartnerClick(sectionId, heading, partner, partnerIndex)
                      }
                    >
                      <span>{partner.largeImage.title}</span>
                    </a>
                  </Row.Column>
                  {/* @ts-ignore*/}
                  <Row.Column size={{ phone: 0, tablet: 4 }}>
                    <div css={styles.PartnersColumn}>
                      <WithImpression
                        once
                        onImpression={() => trackPartnerImpressions(heading, partner)}
                      >
                        {partner.smallImages.map((productImage, imageIndex) => (
                          <a
                            data-testid={PARTNER_BLOCK_SMALL_IMAGE_TESTID}
                            css={styles.PartnersSmallImage}
                            style={
                              {
                                '--background': `url(${productImage.image})`,
                              } as styles.CSSVarSmallImage
                            }
                            key={productImage.title}
                            href={productImage.link}
                            onMouseDown={() =>
                              trackPartnerProductClick(
                                sectionId,
                                heading,
                                partner,
                                partnerIndex,
                                productImage,
                                imageIndex,
                              )
                            }
                          >
                            <span>{productImage.title}</span>
                          </a>
                        ))}
                      </WithImpression>
                    </div>
                  </Row.Column>
                </div>
                <h3 css={Title} style={{ '--topMargin': '18px' } as CSSVarTitle}>
                  {partner.title}
                </h3>
                <p css={SubTitle}>{partner.subtitle}</p>
              </div>
            );
          })}
        </Row>
      </div>
    </>
  );
};
